import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Boxes, FileText, ClipboardList, Trash2, AlertTriangle, ArrowRight,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { useAuth } from "../lib/auth-context";
import { moduleStatusLabel, moduleStatusPillClass, renderStatusLabel, renderStatusPillClass } from "../lib/moduleStatus";

const ADMIN_ROLES = new Set(["ORG_ADMIN", "LD_ADMIN"]);

export default function ManageContent() {
    const { user } = useAuth();
    const nav = useNavigate();
    const isAdmin = ADMIN_ROLES.has(user?.role);

    if (!isAdmin) {
        return (
            <div className="rounded-lg border border-neutral-200 bg-white p-12 text-center text-sm text-neutral-500" data-testid="manage-content-forbidden">
                Manage content is restricted to organisation admins.
            </div>
        );
    }

    return (
        <div className="space-y-8" data-testid="manage-content-page">
            <div>
                <div className="text-[11px] font-medium uppercase tracking-widest text-neutral-500">Content</div>
                <h1 className="mt-2 text-3xl font-medium tracking-tight text-neutral-900">Manage content</h1>
                <p className="mt-1.5 text-sm text-neutral-500">
                    Delete modules and documents in bulk. Every delete cascades through scenes,
                    renders, quizzes, assignments, and files on disk.
                </p>
            </div>

            <Tabs defaultValue="modules" className="space-y-6">
                <TabsList className="bg-neutral-100 p-1">
                    <TabsTrigger value="modules" data-testid="manage-tab-modules" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
                        <Boxes size={13} className="mr-1.5" /> Training modules
                    </TabsTrigger>
                    <TabsTrigger value="documents" data-testid="manage-tab-documents" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
                        <FileText size={13} className="mr-1.5" /> Documents
                    </TabsTrigger>
                </TabsList>
                <TabsContent value="modules"><ModulesPanel nav={nav} /></TabsContent>
                <TabsContent value="documents"><DocumentsPanel nav={nav} /></TabsContent>
            </Tabs>
        </div>
    );
}

// ------------------- Modules panel -------------------

function ModulesPanel({ nav }) {
    const [modules, setModules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState({});
    const [confirmModule, setConfirmModule] = useState(null);
    const [confirmStage, setConfirmStage] = useState(0);
    const [confirmType, setConfirmType] = useState("");
    const [deleting, setDeleting] = useState(false);
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkPreview, setBulkPreview] = useState(null);
    const [bulkType, setBulkType] = useState("");

    const load = async () => {
        try {
            const { data } = await api.get("/modules");
            setModules(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const openConfirm = async (mod, e) => {
        e?.stopPropagation();
        try {
            const { data } = await api.get(`/modules/${mod.id}/delete-preview`);
            setConfirmModule({ module: mod, counts: data.counts });
            setConfirmStage(0); setConfirmType("");
        } catch (err) { toast.error(formatApiError(err)); }
    };
    const doDelete = async () => {
        if (!confirmModule) return;
        setDeleting(true);
        try {
            await api.delete(`/modules/${confirmModule.module.id}`);
            toast.success(`Deleted "${confirmModule.module.name}"`);
            setConfirmModule(null); setConfirmType("");
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setDeleting(false); }
    };
    const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);
    const selectedModules = useMemo(() => modules.filter((m) => selectedIds.includes(m.id)), [modules, selectedIds]);

    const openBulk = async () => {
        const previews = await Promise.all(
            selectedIds.map((id) => api.get(`/modules/${id}/delete-preview`).then((r) => r.data.counts).catch(() => null)),
        );
        const agg = { scenes: 0, chapters: 0, voice_assets: 0, motion_renders: 0,
                      heygen_generations: 0, runway_generations: 0, video_renders: 0,
                      quizzes: 0, assignments: 0, render_jobs: 0 };
        for (const c of previews) {
            if (!c) continue;
            for (const k of Object.keys(agg)) agg[k] += c[k] || 0;
        }
        setBulkPreview(agg); setBulkType(""); setBulkOpen(true);
    };
    const doBulk = async () => {
        setDeleting(true);
        let ok = 0, fail = 0;
        for (const mid of selectedIds) {
            try { await api.delete(`/modules/${mid}`); ok++; } catch { fail++; }
        }
        toast[fail ? "warning" : "success"](`Deleted ${ok} modules${fail ? `, ${fail} failed` : ""}`);
        setSelected({}); setBulkOpen(false); setBulkType(""); setDeleting(false);
        await load();
    };
    const toggleAll = () => {
        if (selectedIds.length === modules.length) setSelected({});
        else setSelected(Object.fromEntries(modules.map((m) => [m.id, true])));
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div className="text-sm text-neutral-500">
                    {loading ? "Loading…" : `${modules.length} module${modules.length === 1 ? "" : "s"}`}
                    {selectedIds.length > 0 && (
                        <span className="ml-2 text-neutral-700">· {selectedIds.length} selected</span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {selectedIds.length > 0 && (
                        <Button
                            variant="outline"
                            onClick={openBulk}
                            className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                            data-testid="manage-modules-bulk-delete-btn"
                        >
                            <Trash2 size={13} className="mr-1.5" /> Delete {selectedIds.length}
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        onClick={() => nav("/content/storyboards")}
                        data-testid="manage-modules-open-storyboards"
                    >
                        Storyboard studio <ArrowRight size={13} className="ml-1.5" />
                    </Button>
                </div>
            </div>

            <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-10">
                                <Checkbox
                                    checked={selectedIds.length > 0 && selectedIds.length === modules.length}
                                    onCheckedChange={toggleAll}
                                    data-testid="manage-modules-select-all"
                                />
                            </TableHead>
                            <TableHead>Module</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Scenes</TableHead>
                            <TableHead>Latest render</TableHead>
                            <TableHead className="text-right">Assigned</TableHead>
                            <TableHead>Updated</TableHead>
                            <TableHead className="w-10"></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody data-testid="manage-modules-table-body">
                        {!loading && modules.length === 0 && (
                            <TableRow><TableCell colSpan={8} className="py-12 text-center text-sm text-neutral-500">No modules yet.</TableCell></TableRow>
                        )}
                        {modules.map((m) => (
                            <TableRow key={m.id} data-testid={`manage-module-row-${m.id}`} className="hover:bg-neutral-50">
                                <TableCell onClick={(e) => e.stopPropagation()}>
                                    <Checkbox
                                        checked={!!selected[m.id]}
                                        onCheckedChange={(v) => setSelected((p) => ({ ...p, [m.id]: !!v }))}
                                        data-testid={`manage-module-select-${m.id}`}
                                    />
                                </TableCell>
                                <TableCell className="cursor-pointer" onClick={() => nav(`/content/storyboards/${m.id}`)}>
                                    <div className="font-medium text-neutral-900">{m.name}</div>
                                    <div className="mono text-[11px] text-neutral-500">{m.document_title}</div>
                                </TableCell>
                                <TableCell>
                                    <span data-testid={`manage-module-status-${m.id}`} title={m.status} className={`inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-medium tracking-wide ${moduleStatusPillClass(m.status)}`}>
                                        {moduleStatusLabel(m.status)}
                                    </span>
                                </TableCell>
                                <TableCell className="text-right mono">{m.scene_count}</TableCell>
                                <TableCell>
                                    <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-medium tracking-wide ${renderStatusPillClass(m.render_status)}`}>
                                        {renderStatusLabel(m.render_status)}
                                    </span>
                                </TableCell>
                                <TableCell className="text-right mono">{m.assignments_count ?? "—"}</TableCell>
                                <TableCell className="mono text-xs text-neutral-500">{new Date(m.updated_at).toLocaleString()}</TableCell>
                                <TableCell className="text-right">
                                    <Button
                                        variant="ghost" size="sm"
                                        onClick={(e) => openConfirm(m, e)}
                                        className="text-neutral-400 hover:text-red-700 hover:bg-red-50"
                                        data-testid={`manage-module-delete-${m.id}`}
                                    >
                                        <Trash2 size={13} />
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            {/* single-delete confirm */}
            <Dialog open={!!confirmModule} onOpenChange={(v) => !v && setConfirmModule(null)}>
                <DialogContent className="sm:max-w-lg" data-testid="manage-module-delete-modal">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-700">
                            <AlertTriangle size={18} /> Delete this module?
                        </DialogTitle>
                        <DialogDescription>
                            Every scene, chapter, generation, render, quiz, assignment, and file
                            this module owns will be permanently deleted. This cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    {confirmModule && (
                        <div className="space-y-3 text-sm">
                            <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3">
                                <div className="font-medium text-neutral-900">{confirmModule.module.name}</div>
                                <div className="mt-2 text-[12px] text-neutral-700">
                                    This will delete{" "}
                                    <span className="mono font-medium">{confirmModule.counts.scenes}</span> scenes,{" "}
                                    <span className="mono font-medium">{confirmModule.counts.video_renders}</span> renders,{" "}
                                    <span className="mono font-medium">{confirmModule.counts.quizzes}</span> quiz,{" "}
                                    <span className="mono font-medium">{confirmModule.counts.assignments}</span> assignments,{" "}
                                    <span className="mono font-medium">{confirmModule.counts.voice_assets}</span> voiceovers,{" "}
                                    <span className="mono font-medium">{confirmModule.counts.motion_renders}</span> motion clips,{" "}
                                    <span className="mono font-medium">{confirmModule.counts.heygen_generations + confirmModule.counts.runway_generations}</span> AI generations.
                                </div>
                            </div>
                            {confirmStage === 0 ? (
                                <Button
                                    variant="outline" className="w-full border-red-200 text-red-700 hover:bg-red-50"
                                    onClick={() => setConfirmStage(1)}
                                    data-testid="manage-module-delete-continue"
                                >
                                    I understand — continue
                                </Button>
                            ) : (
                                <>
                                    <div className="text-xs text-neutral-600">Type <span className="mono font-semibold">DELETE</span> to confirm.</div>
                                    <Input value={confirmType} onChange={(e) => setConfirmType(e.target.value)} placeholder="DELETE" autoFocus data-testid="manage-module-delete-input" />
                                    <Button
                                        onClick={doDelete}
                                        disabled={confirmType !== "DELETE" || deleting}
                                        className="w-full bg-red-600 text-white hover:bg-red-700 disabled:opacity-40"
                                        data-testid="manage-module-delete-confirm"
                                    >
                                        {deleting ? "Deleting…" : "Yes, delete permanently"}
                                    </Button>
                                </>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* bulk-delete confirm */}
            <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
                <DialogContent className="sm:max-w-lg" data-testid="manage-module-bulk-delete-modal">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-700">
                            <AlertTriangle size={18} /> Delete {selectedIds.length} modules?
                        </DialogTitle>
                        <DialogDescription>
                            Aggregated cascade below. Every listed asset will be permanently deleted.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 text-sm">
                        {bulkPreview && (
                            <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3 text-[12px] text-neutral-700">
                                Total across selected: <span className="mono font-medium">{bulkPreview.scenes}</span> scenes,{" "}
                                <span className="mono font-medium">{bulkPreview.video_renders}</span> renders,{" "}
                                <span className="mono font-medium">{bulkPreview.quizzes}</span> quizzes,{" "}
                                <span className="mono font-medium">{bulkPreview.assignments}</span> assignments,{" "}
                                <span className="mono font-medium">{bulkPreview.voice_assets}</span> voiceovers,{" "}
                                <span className="mono font-medium">{bulkPreview.motion_renders}</span> motion clips,{" "}
                                <span className="mono font-medium">{bulkPreview.heygen_generations + bulkPreview.runway_generations}</span> AI generations.
                            </div>
                        )}
                        <div className="max-h-32 overflow-y-auto rounded-md border border-neutral-200 bg-neutral-50 p-3 text-[12px] text-neutral-700">
                            {selectedModules.map((m) => <div key={m.id}>{m.name}</div>)}
                        </div>
                        <div className="text-xs text-neutral-600">Type <span className="mono font-semibold">DELETE</span> to confirm.</div>
                        <Input value={bulkType} onChange={(e) => setBulkType(e.target.value)} placeholder="DELETE" autoFocus data-testid="manage-module-bulk-input" />
                        <Button
                            onClick={doBulk}
                            disabled={bulkType !== "DELETE" || deleting}
                            className="w-full bg-red-600 text-white hover:bg-red-700 disabled:opacity-40"
                            data-testid="manage-module-bulk-confirm"
                        >
                            {deleting ? "Deleting…" : "Yes, delete permanently"}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// ------------------- Documents panel -------------------

function DocumentsPanel({ nav }) {
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState({});
    const [confirmDoc, setConfirmDoc] = useState(null);
    const [confirmStage, setConfirmStage] = useState(0);
    const [confirmType, setConfirmType] = useState("");
    const [deleting, setDeleting] = useState(false);
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkPreview, setBulkPreview] = useState(null);
    const [bulkType, setBulkType] = useState("");

    const load = async () => {
        try {
            const { data } = await api.get("/documents");
            setDocs(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const openConfirm = async (doc, e) => {
        e?.stopPropagation();
        try {
            const { data } = await api.get(`/documents/${doc.id}/delete-preview`);
            setConfirmDoc({ doc, counts: data.counts });
            setConfirmStage(0); setConfirmType("");
        } catch (err) { toast.error(formatApiError(err)); }
    };
    const doDelete = async () => {
        if (!confirmDoc) return;
        setDeleting(true);
        try {
            await api.delete(`/documents/${confirmDoc.doc.id}`);
            toast.success(`Deleted "${confirmDoc.doc.parsed_title || confirmDoc.doc.original_filename}"`);
            setConfirmDoc(null); setConfirmType(""); await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setDeleting(false); }
    };
    const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);
    const selectedDocs = useMemo(() => docs.filter((d) => selectedIds.includes(d.id)), [docs, selectedIds]);

    const openBulk = async () => {
        const previews = await Promise.all(
            selectedIds.map((id) => api.get(`/documents/${id}/delete-preview`).then((r) => r.data.counts).catch(() => null)),
        );
        const agg = { procedure_steps: 0, media_assets: 0, document_versions: 0, modules_built_from: 0 };
        for (const c of previews) {
            if (!c) continue;
            for (const k of Object.keys(agg)) agg[k] += c[k] || 0;
        }
        setBulkPreview(agg); setBulkType(""); setBulkOpen(true);
    };
    const doBulk = async () => {
        setDeleting(true);
        let ok = 0, fail = 0;
        for (const did of selectedIds) {
            try { await api.delete(`/documents/${did}`); ok++; } catch { fail++; }
        }
        toast[fail ? "warning" : "success"](`Deleted ${ok} documents${fail ? `, ${fail} failed` : ""}`);
        setSelected({}); setBulkOpen(false); setBulkType(""); setDeleting(false);
        await load();
    };
    const toggleAll = () => {
        if (selectedIds.length === docs.length) setSelected({});
        else setSelected(Object.fromEntries(docs.map((d) => [d.id, true])));
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div className="text-sm text-neutral-500">
                    {loading ? "Loading…" : `${docs.length} document${docs.length === 1 ? "" : "s"}`}
                    {selectedIds.length > 0 && (
                        <span className="ml-2 text-neutral-700">· {selectedIds.length} selected</span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {selectedIds.length > 0 && (
                        <Button
                            variant="outline"
                            onClick={openBulk}
                            className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                            data-testid="manage-docs-bulk-delete-btn"
                        >
                            <Trash2 size={13} className="mr-1.5" /> Delete {selectedIds.length}
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        onClick={() => nav("/knowledge/documents")}
                        data-testid="manage-docs-open-list"
                    >
                        Document library <ArrowRight size={13} className="ml-1.5" />
                    </Button>
                </div>
            </div>

            <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-10">
                                <Checkbox
                                    checked={selectedIds.length > 0 && selectedIds.length === docs.length}
                                    onCheckedChange={toggleAll}
                                    data-testid="manage-docs-select-all"
                                />
                            </TableHead>
                            <TableHead>Title</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Steps</TableHead>
                            <TableHead className="text-right">Images</TableHead>
                            <TableHead>Updated</TableHead>
                            <TableHead className="w-10"></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody data-testid="manage-docs-table-body">
                        {!loading && docs.length === 0 && (
                            <TableRow><TableCell colSpan={8} className="py-12 text-center text-sm text-neutral-500">No documents yet.</TableCell></TableRow>
                        )}
                        {docs.map((d) => (
                            <TableRow key={d.id} data-testid={`manage-doc-row-${d.id}`} className="hover:bg-neutral-50">
                                <TableCell onClick={(e) => e.stopPropagation()}>
                                    <Checkbox
                                        checked={!!selected[d.id]}
                                        onCheckedChange={(v) => setSelected((p) => ({ ...p, [d.id]: !!v }))}
                                        data-testid={`manage-doc-select-${d.id}`}
                                    />
                                </TableCell>
                                <TableCell className="cursor-pointer" onClick={() => nav(`/knowledge/documents/${d.id}`)}>
                                    <div className="font-medium text-neutral-900">{d.parsed_title || d.original_filename}</div>
                                    <div className="mono text-[11px] text-neutral-500">{d.parsed_doc_number || d.original_filename}</div>
                                </TableCell>
                                <TableCell className="text-neutral-700">{d.type}</TableCell>
                                <TableCell>
                                    <span className="inline-flex items-center rounded-md bg-neutral-100 px-2 py-0.5 text-[10px] font-medium text-neutral-700">
                                        {d.status?.replaceAll("_", " ")}
                                    </span>
                                </TableCell>
                                <TableCell className="text-right mono">{d.step_count}</TableCell>
                                <TableCell className="text-right mono">{d.media_count}</TableCell>
                                <TableCell className="mono text-xs text-neutral-500">{new Date(d.updated_at).toLocaleString()}</TableCell>
                                <TableCell className="text-right">
                                    <Button
                                        variant="ghost" size="sm"
                                        onClick={(e) => openConfirm(d, e)}
                                        className="text-neutral-400 hover:text-red-700 hover:bg-red-50"
                                        data-testid={`manage-doc-delete-${d.id}`}
                                    >
                                        <Trash2 size={13} />
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            {/* single-delete confirm */}
            <Dialog open={!!confirmDoc} onOpenChange={(v) => !v && setConfirmDoc(null)}>
                <DialogContent className="sm:max-w-lg" data-testid="manage-doc-delete-modal">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-700">
                            <AlertTriangle size={18} /> Delete this document?
                        </DialogTitle>
                        <DialogDescription>
                            Every parsed step, image, and training module built from it will be
                            permanently deleted. This cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    {confirmDoc && (
                        <div className="space-y-3 text-sm">
                            <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3">
                                <div className="font-medium text-neutral-900">{confirmDoc.doc.parsed_title || confirmDoc.doc.original_filename}</div>
                                <div className="mt-2 text-[12px] text-neutral-700">
                                    This will delete{" "}
                                    <span className="mono font-medium">{confirmDoc.counts.procedure_steps}</span> steps,{" "}
                                    <span className="mono font-medium">{confirmDoc.counts.media_assets}</span> images,{" "}
                                    <span className="mono font-medium">{confirmDoc.counts.document_versions}</span> versions,
                                    {confirmDoc.counts.modules_built_from > 0 && (
                                        <>
                                            {" "}and cascade-delete{" "}
                                            <span className="mono font-medium text-red-700">{confirmDoc.counts.modules_built_from}</span> module(s) built from it.
                                        </>
                                    )}
                                </div>
                            </div>
                            {confirmStage === 0 ? (
                                <Button
                                    variant="outline" className="w-full border-red-200 text-red-700 hover:bg-red-50"
                                    onClick={() => setConfirmStage(1)}
                                    data-testid="manage-doc-delete-continue"
                                >
                                    I understand — continue
                                </Button>
                            ) : (
                                <>
                                    <div className="text-xs text-neutral-600">Type <span className="mono font-semibold">DELETE</span> to confirm.</div>
                                    <Input value={confirmType} onChange={(e) => setConfirmType(e.target.value)} placeholder="DELETE" autoFocus data-testid="manage-doc-delete-input" />
                                    <Button
                                        onClick={doDelete}
                                        disabled={confirmType !== "DELETE" || deleting}
                                        className="w-full bg-red-600 text-white hover:bg-red-700 disabled:opacity-40"
                                        data-testid="manage-doc-delete-confirm"
                                    >
                                        {deleting ? "Deleting…" : "Yes, delete permanently"}
                                    </Button>
                                </>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* bulk-delete confirm */}
            <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
                <DialogContent className="sm:max-w-lg" data-testid="manage-doc-bulk-delete-modal">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-700">
                            <AlertTriangle size={18} /> Delete {selectedIds.length} documents?
                        </DialogTitle>
                        <DialogDescription>
                            Aggregated cascade below.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 text-sm">
                        {bulkPreview && (
                            <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3 text-[12px] text-neutral-700">
                                Total across selected: <span className="mono font-medium">{bulkPreview.procedure_steps}</span> steps,{" "}
                                <span className="mono font-medium">{bulkPreview.media_assets}</span> images,{" "}
                                <span className="mono font-medium">{bulkPreview.document_versions}</span> versions,{" "}
                                <span className="mono font-medium text-red-700">{bulkPreview.modules_built_from}</span> modules built from them.
                            </div>
                        )}
                        <div className="max-h-32 overflow-y-auto rounded-md border border-neutral-200 bg-neutral-50 p-3 text-[12px] text-neutral-700">
                            {selectedDocs.map((d) => <div key={d.id}>{d.parsed_title || d.original_filename}</div>)}
                        </div>
                        <div className="text-xs text-neutral-600">Type <span className="mono font-semibold">DELETE</span> to confirm.</div>
                        <Input value={bulkType} onChange={(e) => setBulkType(e.target.value)} placeholder="DELETE" autoFocus data-testid="manage-doc-bulk-input" />
                        <Button
                            onClick={doBulk}
                            disabled={bulkType !== "DELETE" || deleting}
                            className="w-full bg-red-600 text-white hover:bg-red-700 disabled:opacity-40"
                            data-testid="manage-doc-bulk-confirm"
                        >
                            {deleting ? "Deleting…" : "Yes, delete permanently"}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
